import React from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { getNewNotification, markNotificationAsRead, clearAllNotifications, type NotificationType } from "../api/notification";
import { useAuth } from "../hooks/auth";

interface NotificationContextType {
    notifications: NotificationType[];
    unreadCount: number;
    markAsRead: (notificationId: string) => Promise<void>;
    clearAll: () => Promise<void>;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
}

export const NotificationContext = React.createContext<NotificationContextType>(null!);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, userId } = useAuth();
    const queryClient = useQueryClient();

    const { data, isLoading, isError, refetch } = useQuery(
        ["notifications", userId],
        getNewNotification,
        { enabled: isAuthenticated }
    );

    const invalidate = () => queryClient.invalidateQueries(["notifications"]);

    const markAsReadMutation = useMutation(markNotificationAsRead, { onSuccess: invalidate });
    const clearAllMutation = useMutation(clearAllNotifications, { onSuccess: invalidate });

    const allNotifications: NotificationType[] = data?.data?.data ?? [];
    const notifications = isAuthenticated
        ? allNotifications.filter((n: NotificationType) => !n.read_at)
        : [];

    const value: NotificationContextType = {
        notifications,
        unreadCount: notifications.length,
        markAsRead: async (notificationId) => {
            await markAsReadMutation.mutateAsync(notificationId);
        },
        clearAll: async () => {
            await clearAllMutation.mutateAsync();
        },
        isLoading,
        isError,
        refetch,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export type { NotificationType };
